import React, { useEffect, useRef, useState, useCallback } from 'react'
import PageHeader from '../utils/PageHeader'
import { API_URL } from '../utils/Constants/URLS'
import PageLoader from '../utils/PageLoader'

// LeadDetailsReport: shows lead rows returned from
// API: `${API_URL}leads/report?pagination=true&limit=${limit}&page=${p}&startDate=${s}&endDate=${e}`
export default function LeadDetailsReport () {
  const token = localStorage.getItem('token')
  const _now = new Date()
  const _year = _now.getFullYear()
  const _month = String(_now.getMonth() + 1).padStart(2, '0')
  const defaultStart = `${_year}-${_month}-01`
  const defaultEnd = _now.toISOString().slice(0, 10) // YYYY-MM-DD

  // state
  const [data, setData] = useState([])
  const [startDate, setStartDate] = useState(defaultStart)
  const [endDate, setEndDate] = useState(defaultEnd)
  const [loading, setLoading] = useState(false) // full reload
  const [pageLoading, setPageLoading] = useState(false) // incremental loads
  const [error, setError] = useState(null)

  // pagination / lazy load
  const [page, setPage] = useState(1)
  const limit = 50
  const [hasMore, setHasMore] = useState(true)
  const observerRef = useRef(null)

  // Build API URL for page & date range
  const buildUrl = (p = page) => {
    const s = encodeURIComponent(startDate)
    const e = encodeURIComponent(endDate)
    return `${API_URL}leads/report?pagination=false&page=${p}&startDate=${s}&endDate=${e}`
  }

  // Fetch a specific page (used for initial load and subsequent lazy loads)
  const fetchPage = useCallback(
    async (p = 1, replace = false) => {
      try {
        if (p === 1 && replace) setLoading(true)
        else setPageLoading(true)
        setError(null)

        const res = await fetch(buildUrl(p), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (!res.ok) throw new Error(`Server responded with ${res.status}`)
        const result = await res.json()

        // API shape assumed when pagination=true:
        // { error:false, data: { docs: [ ... ] , totalDocs, totalPages } }
        // but sometimes data might directly be an array (defensive)
        const docs = result?.data?.reportData

        // ensure docs is an array
        const docsArray = Array.isArray(docs) ? docs : []

        setData(prev => (p === 1 && replace ? docsArray : [...prev, ...docsArray]))

        // detect whether more pages present
        if (docsArray.length < limit) setHasMore(false)
        else setHasMore(true)
      } catch (err) {
        setError(err.message || 'Failed to fetch')
      } finally {
        setLoading(false)
        setPageLoading(false)
      }
    },
    // dependencies: token, startDate, endDate are captured by buildUrl (kept simple)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, startDate, endDate]
  )

  // initial load (or when date range changes)
  useEffect(() => {
    setPage(1)
    fetchPage(1, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, startDate, endDate])

  // IntersectionObserver for sentinel -> load next page
  useEffect(() => {
    if (!hasMore) return
    const sentinel = observerRef.current
    if (!sentinel) return

    const io = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !pageLoading && !loading) {
            setPage(prev => prev + 1)
          }
        })
      },
      { root: null, rootMargin: '200px', threshold: 0.1 }
    )

    io.observe(sentinel)
    return () => io.disconnect()
  }, [hasMore, pageLoading, loading])

  // when page changes (and page > 1) fetch that page
  useEffect(() => {
    if (page === 1) return
    fetchPage(page)
  }, [page, fetchPage])

  const handleFetchClick = () => {
    if (startDate > endDate) {
      setError('Start date cannot be after end date')
      return
    }
    setError(null)
    setPage(1)
    fetchPage(1, true)
  }

  const handleReset = () => {
    setStartDate(defaultStart)
    setEndDate(defaultEnd)
    setError(null)
    setPage(1)
    fetchPage(1, true)
  }

  // ---------- Helpers ----------
  // safe date formatting (returns empty string if falsy)
  const formatDate = iso => {
    if (!iso) return ''
    try {
      return new Date(iso).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    } catch {
      return ''
    }
  }

  // CSV quoting helper
  const quote = value => {
    if (value === null || value === undefined) return '""'
    const str = String(value)
    return `"${str.replace(/"/g, '""')}"`
  }

  // ---------- CSV setup ----------
  // CSV header labels (match columns rendered in table)
  const csvHeaderLabels = [
    'Area',
    'Sales Executive',
    'Business Line',
    'Type of Industry',
    'Lead ID',
    'Source of Enquiry',
    'Lead Creation Date',
    'Customer Name',
    'Product Line',
    'Product Name',
    'Part Code',
    'Quantity',
    'Proposed Value',
    'Line Item Status',
    'Expected Closure Date',
    'Type of Sale (Direct/ GSPL)',
    'New/ Existing Customer',
    'Present Platform',
    'Last Modified Date',
    'Age (days)',
    'Current Stage Duration (days)',
    'Competitions'
  ]

  // build CSV and trigger download (quotes every field)
  const downloadCsv = () => {
    if (!data || data.length === 0) {
      setError('No data to download for the selected range.')
      return
    }
    setError(null)

    // header row (quote headers too)
    const headerRow = csvHeaderLabels.map(h => quote(h)).join(',')

    // build rows in same order as headers
    const csvLines = data.map(dt => {
      const rowObj = {
        Area: Array.isArray(dt?.businessArea) ? dt.businessArea.join(', ') : dt?.businessArea ?? '',
        'Sales Executive': dt?.executiveName ?? '',
        'Business Line': dt?.businessLine ?? '',
        'Type of Industry': dt?.typeOfIndustry ?? '', // not present in sample -> blank if missing
        'Lead ID': dt?.leadId ?? '',
        'Source of Enquiry': dt?.sourceOfEnquiry ?? '',
        'Lead Creation Date': dt?.createdAt ? formatDate(dt.createdAt) : '',
        'Customer Name': dt?.customerName ?? '',
        'Product Line': dt?.productLine ?? '',
        'Product Name': dt?.modelName ?? '',
        'Part Code': dt?.partCode ?? '',
        'Quantity': dt?.quantity ?? '',
        'Proposed Value': dt?.proposedDealAmount ?? dt?.value ?? '',
        'Line Item Status': dt?.leadStatus ?? '',
        'Expected Closure Date': dt?.expectedClosureDate ? formatDate(dt.expectedClosureDate) : '',
        'Type of Sale (Direct/ GSPL)': dt?.leadType ?? '',
        'New/ Existing Customer': dt?.customerType ?? '',
        'Present Platform': dt?.stage ?? '', // not present in sample -> blank if missing
        'Last Modified Date': dt?.updatedAt ? formatDate(dt.updatedAt) : '',
        'Age (days)': dt?.age ? Math.round(dt.age) : '',
        'Current Stage Duration (days)': dt?.currentStageDuration ?? '',
        'Competitions': Array.isArray(dt?.competitors) ? dt.competitors.join(', ') : dt?.competitors ?? ''
      }

      const values = csvHeaderLabels.map(h => quote(rowObj[h]))
      return values.join(',')
    })

    const csvContent = [headerRow, ...csvLines].join('\r\n')

    // create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `lead_details_${startDate}_to_${endDate}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <PageHeader
        pageName='LEAD DETAILS'
        breadcrump1='Reports'
        breadcrump2='Lead Details'
        link='/lead-report'
      />

      <div className='p-6 min-h-screen'>
        {/* date selectors */}
        <div className='max-w-[1400px] mx-2 mb-4 bg-white border border-gray-200 p-4 rounded-md'>
          <div className='flex flex-col md:flex-row md:items-end md:justify-between gap-3'>
            <div className='flex flex-col md:flex-row md:items-end gap-3'>
              <div className='flex items-center gap-2'>
                <label className='text-sm font-medium'>Start Date</label>
                <input
                  type='date'
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className='px-2 py-1 border rounded'
                  max={endDate}
                />
              </div>

              <div className='flex items-center gap-2'>
                <label className='text-sm font-medium'>End Date</label>
                <input
                  type='date'
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className='px-2 py-1 border rounded'
                  min={startDate}
                />
              </div>

              <div className='flex items-center gap-2 mt-2 md:mt-0'>
                <button
                  onClick={handleFetchClick}
                  className='px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700'
                >
                  Fetch
                </button>
                <button
                  onClick={handleReset}
                  className='px-4 py-2 border rounded hover:bg-gray-50'
                >
                  Reset
                </button>
              </div>
            </div>

            <div className='flex items-center justify-end'>
              <button
                onClick={downloadCsv}
                className='px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700'
              >
                Download
              </button>
            </div>
          </div>

          {error && <div className='mt-3 text-sm text-red-600'>{error}</div>}
        </div>

        <div className='overflow-x-auto bg-white border border-gray-200 max-w-[1400px] mx-2 relative'>
          {/* PageLoader overlay when performing a large reload */}
          {loading && (
            <div className='absolute inset-0 z-50 flex items-center justify-center bg-white/60'>
              <PageLoader />
            </div>
          )}

          <table className='min-w-full border-collapse table-auto'>
            <thead className='bg-gradient-to-r from-indigo-500 to-indigo-600  text-white text-sm font-semibold uppercase tracking-wide'>
              <tr>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Area</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Sales Executive</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Business Line</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Type of Industry</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Lead ID</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Source of Enquiry</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Lead Creation Date</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Customer Name</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Product Line</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Product Name</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Part Code</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Quantity</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Proposed Value</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Line Item Status</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Expected Closure Date</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Type of Sale (Direct/ GSPL)</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>New/ Existing Customer</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Present Platform</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Last Modified Date</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Age (days)</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Current Stage Duration (days)</th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>Competitions</th>
              </tr>
            </thead>

            <tbody className='text-sm text-gray-800'>
              {data.map((row) => {
                // each row maps fields from API sample
                const id = row?._id ?? row?.leadId ?? Math.random().toString(36).slice(2)
                return (
                  <tr key={id} className='hover:bg-gray-50'>
                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {/* businessArea is array in sample */}
                      {Array.isArray(row?.businessArea) ? row.businessArea.join(', ') : row?.businessArea ?? '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {row?.executiveName ?? '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {row?.businessLine ?? '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {/* Type of industry not present in sample data -> show dash if missing */}
                      {row?.typeOfIndustry ?? '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {row?.leadId ?? '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {row?.sourceOfEnquiry ?? '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {row?.createdAt ? formatDate(row.createdAt) : '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {row?.customerName ?? '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {row?.productLine ?? '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {row?.modelName ?? '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {row?.partCode ?? '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {row?.quantity ?? '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {/* prefer proposedDealAmount, fallback to value */}
                      {row?.proposedDealAmount ?? row?.value ?? '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {row?.leadStatus ? row.leadStatus.toUpperCase() : '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {row?.expectedClosureDate ? formatDate(row.expectedClosureDate) : '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {row?.leadType ?? '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {row?.customerType ?? '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      
                      {row?.stage ?? '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {row?.updatedAt ? formatDate(row.updatedAt) : '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {row?.age != null ? Math.round(row.age) : '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {row?.currentStageDuration ?? '-'}
                    </td>

                    <td className='px-3 py-2 border-b border-gray-200 whitespace-nowrap'>
                      {Array.isArray(row?.competitors) ? row.competitors.join(', ') : row?.competitors ?? '-'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {/* sentinel for intersection observer lazy-load */}
          <div
            ref={observerRef}
            className='h-8 flex items-center justify-center'
          >
            {pageLoading && <PageLoader />}
            {!hasMore && !loading && (
              <div className='p-4 text-center text-sm text-gray-500'>
                No more records.
              </div>
            )}
          </div>

          {!loading && data.length === 0 && !error && (
            <div className='p-6 text-center text-sm text-gray-600'>
              No records found for selected date range.
            </div>
          )}
        </div>
      </div>
    </>
  )
}
